import { action, observable, makeObservable } from 'mobx';
import { enableStaticRendering } from 'mobx-react';
import { useMemo } from 'react';

import { FilterStore } from './filterStore';
import { setCookie } from './Util';

enableStaticRendering(typeof window === 'undefined');

/**
 * @typedef {{
 *  accessToken: string,
 *  fullname: string,
 *  organizations: import('../components/SideMenu').Organization[],
 *  organizationsToContract: import('../components/SideMenu').Organization[],
 *  selectedOrganizationId: number,
 *  prevSelectedOrganizationId: number,
 *  acceptedTerms: boolean,
 *  notAuthorized: boolean,
 * }} User
 */

/**
 * @typedef {{
 *  user: User,
 *  errorMessage: string | null,
 *  modal: observable,
 *  isEnrollmentActive: observable,
 *  isMenuExpanded: observable,
 *  alert: observable,
 *  currency: observable,
 *  setUser: (user: User) => void,
 *  setErrorMessage: (errorMessage: string?) => void,
 *  setModal: (modal: any) => void,
 *  setIsEnrollmentActive: (isEnrollmentActive: any) => void,
 *  setIsMenuExpanded: (isMenuExpanded: any) => void,
 *  setAlert: (alert: any) => void,
 *  logout: () => void,
 *  changeSelectedOrganization: (
 *    organization: import('../components/SideMenu').Organization,
 *    skipRedirect?: boolean,
 *  ) => void
 * }} PopulatedStore
 */

/** @type {PopulatedStore} */
let store;

export class Store {
  user = observable({});

  errorMessage = observable.box(null);

  modal = observable({});

  isEnrollmentActive = observable.box(null);

  isMenuExpanded = observable.box(null);

  alert = observable.box(null);

  currency = observable.box('BRL');

  selectedEntities = observable([]);

  /** @param {any} user */
  setUser(user) {
    if (this) this.user = user;
  }

  /** @param {any} errorMessage */
  setErrorMessage(errorMessage) {
    this.errorMessage = errorMessage;
  }

  /** @param {any} modal */
  setModal(modal) {
    this.modal = modal;
  }

  /** @param {any} isEnrollmentActive */
  setIsEnrollmentActive(isEnrollmentActive) {
    this.isEnrollmentActive = isEnrollmentActive;
  }

  /** @param {any} isMenuExpanded */
  setIsMenuExpanded(isMenuExpanded) {
    this.isMenuExpanded = isMenuExpanded;
    setCookie(null, 'isMenuExpanded', isMenuExpanded);
  }

  /** @param {any} alert */
  setAlert(alert) {
    this.alert = alert;
  }

  /** @param {any} selectedEntities */
  setSelectedEntities(selectedEntities) {
    this.selectedEntities = selectedEntities;
  }

  reset() {
    this.setUser({});
    this.setErrorMessage(null);
    this.setModal({});
    this.setIsEnrollmentActive(null);
    this.setIsMenuExpanded(null);
    this.setSelectedEntities(null);
  }

  constructor() {
    makeObservable(this, {
      user: observable,
      errorMessage: observable,
      modal: observable,
      isEnrollmentActive: observable,
      selectedEntities: observable,
      setUser: action,
      setErrorMessage: action,
      setModal: action,
      setIsEnrollmentActive: action,
      setIsMenuExpanded: action
    });
    //@ts-ignore - Todo fix mobx typing and create rootStore to save both this store others stores(i.e. FilterStore) instead of saving in this one
    this.filterStore = new FilterStore();
  }
}

/** @param {Partial<Store> | null} initialData */
export const initializeStore = (initialData = null) => {
  const _store = store ?? new Store();

  // If your page has Next.js data fetching methods that use a Mobx store, it will
  // get hydrated here, check `pages/ssg.js` and `pages/ssr.js` for more details
  if (initialData) {
    //@ts-ignore - TODO fix hydrate doesn't exist in Store
    _store.hydrate(initialData);
  }
  // For SSG and SSR always create a new store
  if (typeof window === 'undefined') return _store;
  // Create the store once in the client
  if (!store) store = _store;

  return _store;
};

/** @param {Partial<Store> | null} initialState */
export function useStore(initialState) {
  const store = useMemo(() => initializeStore(initialState), [initialState]);
  return store;
}
